import api from './api';
import type {
  SubjectPayload,
  SubjectResource,
  SubjectUpdatePayload,
  TopicPayload,
  TopicResource,
  TopicUpdatePayload,
  SubTopicPayload,
  SubTopicResource,
  SubTopicUpdatePayload,
  TagPayload,
  TagResource,
  TagUpdatePayload,
  TestSeriesPayload,
  TestSeriesResource,
  TestSeriesUpdatePayload,
  GroupTypePayload,
  GroupTypeResource,
  GroupTypeUpdatePayload,
} from '../types';

export const fetchSubjects = async (signal?: AbortSignal): Promise<SubjectResource[]> => {
  const response = await api.get('/taxonomies/subjects', { signal });
  return response.data.data;
};

export const fetchTopics = async (
  params: { subjectId?: string } = {},
  signal?: AbortSignal
): Promise<TopicResource[]> => {
  const response = await api.get('/taxonomies/topics', {
    params,
    signal,
  });
  return response.data.data;
};

export const fetchSubTopics = async (
  params: { subjectId?: string; topicId?: string } = {},
  signal?: AbortSignal
): Promise<SubTopicResource[]> => {
  const response = await api.get('/taxonomies/sub-topics', {
    params,
    signal,
  });
  return response.data.data;
};

export const fetchTags = async (
  params: { isActive?: boolean } = {},
  signal?: AbortSignal
): Promise<TagResource[]> => {
  const response = await api.get('/taxonomies/tags', {
    params,
    signal,
  });
  return response.data.data;
};

export const fetchTestSeries = async (
  params: { isActive?: boolean } = {},
  signal?: AbortSignal
): Promise<TestSeriesResource[]> => {
  const response = await api.get('/taxonomies/test-series', {
    params,
    signal,
  });
  return response.data.data;
};

export const createSubject = async (payload: SubjectPayload): Promise<SubjectResource> => {
  const response = await api.post('/taxonomies/subjects', payload);
  return response.data.data;
};

export const updateSubject = async (
  id: string,
  payload: SubjectUpdatePayload
): Promise<SubjectResource> => {
  const response = await api.put(`/taxonomies/subjects/${id}`, payload);
  return response.data.data;
};

export const createTopic = async (payload: TopicPayload): Promise<TopicResource> => {
  const response = await api.post('/taxonomies/topics', payload);
  return response.data.data;
};

export const updateTopic = async (
  id: string,
  payload: TopicUpdatePayload
): Promise<TopicResource> => {
  const response = await api.put(`/taxonomies/topics/${id}`, payload);
  return response.data.data;
};

export const createSubTopic = async (
  payload: SubTopicPayload
): Promise<SubTopicResource> => {
  const response = await api.post('/taxonomies/sub-topics', payload);
  return response.data.data;
};

export const updateSubTopic = async (
  id: string,
  payload: SubTopicUpdatePayload
): Promise<SubTopicResource> => {
  const response = await api.put(`/taxonomies/sub-topics/${id}`, payload);
  return response.data.data;
};

export const createTag = async (payload: TagPayload): Promise<TagResource> => {
  const response = await api.post('/taxonomies/tags', payload);
  return response.data.data;
};

export const updateTag = async (id: string, payload: TagUpdatePayload): Promise<TagResource> => {
  const response = await api.put(`/taxonomies/tags/${id}`, payload);
  return response.data.data;
};

export const createTestSeries = async (
  payload: TestSeriesPayload
): Promise<TestSeriesResource> => {
  const response = await api.post('/taxonomies/test-series', payload);
  return response.data.data;
};

export const updateTestSeries = async (
  id: string,
  payload: TestSeriesUpdatePayload
): Promise<TestSeriesResource> => {
  const response = await api.put(`/taxonomies/test-series/${id}`, payload);
  return response.data.data;
};

export const fetchGroupTypes = async (
  params: { isActive?: boolean } = {},
  signal?: AbortSignal
): Promise<GroupTypeResource[]> => {
  const response = await api.get('/taxonomies/group-types', {
    params,
    signal,
  });
  return response.data.data;
};

export const createGroupType = async (
  payload: GroupTypePayload
): Promise<GroupTypeResource> => {
  const response = await api.post('/taxonomies/group-types', payload);
  return response.data.data;
};

export const updateGroupType = async (
  id: string,
  payload: GroupTypeUpdatePayload
): Promise<GroupTypeResource> => {
  const response = await api.put(`/taxonomies/group-types/${id}`, payload);
  return response.data.data;
};

export const deleteSubject = async (id: string): Promise<void> => {
  await api.delete(`/taxonomies/subjects/${id}`);
};

export const deleteTopic = async (id: string): Promise<void> => {
  await api.delete(`/taxonomies/topics/${id}`);
};

export const deleteSubTopic = async (id: string): Promise<void> => {
  await api.delete(`/taxonomies/sub-topics/${id}`);
};

export const deleteTag = async (id: string): Promise<void> => {
  await api.delete(`/taxonomies/tags/${id}`);
};

export const deleteTestSeries = async (id: string): Promise<void> => {
  await api.delete(`/taxonomies/test-series/${id}`);
};

export const deleteGroupType = async (id: string): Promise<void> => {
  await api.delete(`/taxonomies/group-types/${id}`);
};
